/**
 * Centralized Permission Logic
 *
 * Replaces scattered `userRole === "Admin"` checks with a unified
 * permission system that combines role + editor permissions + tier features.
 *
 * Role hierarchy: Admin/Owner > Manager > Editor > User
 * Key rule: Subscription tier ALWAYS overrides role permissions.
 */

import {
  Permission,
  EditorPermissions,
  UserRole,
  SYSTEM_ADMIN_ROLES,
  DEFAULT_EDITOR_PERMISSIONS,
} from '../types/company';
import { TierFeatures } from './tierFeatures';

/**
 * Map of permissions to the tier features they require.
 * If a permission is not listed here, it doesn't require a tier feature.
 */
const PERMISSION_TIER_MAP: Partial<Record<Permission, keyof TierFeatures>> = {
  viewAnalytics: 'analytics',
  exportData: 'excelExport',
  manageApprovals: 'approvalSystem',
  useLandingPage: 'landingPageBuilder',
};

/**
 * Map of permissions to the editor permission flags they require.
 */
const PERMISSION_EDITOR_MAP: Partial<Record<Permission, keyof EditorPermissions>> = {
  editForms: 'canEditForms',
  viewSubmissions: 'canViewSubmissions',
  shareForms: 'canShareForms',
  archiveSubmissions: 'canArchiveSubmissions',
  viewAnalytics: 'canViewAnalytics',
  exportData: 'canExportData',
  manageApprovals: 'canManageApprovals',
  useLandingPage: 'canUseLandingPage',
  viewSecureDetails: 'canViewSecureDetails',
};

/**
 * Check if a user has a specific permission.
 *
 * @param role - The user's role
 * @param permission - The permission to check
 * @param editorPermissions - The company's editor permission config (optional for non-Editors)
 * @param tierFeatures - The company's tier features (optional, skips tier check if not provided)
 * @returns true if the user has the permission
 */
export function hasPermission(
  role: UserRole | string | null | undefined,
  permission: Permission,
  editorPermissions?: EditorPermissions | null,
  tierFeatures?: TierFeatures | null
): boolean {
  if (!role) return false;

  // useLandingPage: purely tier-driven — show only when subscription tier has landingPageBuilder true
  if (permission === 'useLandingPage') {
    return tierFeatures?.landingPageBuilder === true;
  }

  // Admin/Owner: always has all permissions (except useLandingPage, handled above)
  if (SYSTEM_ADMIN_ROLES.includes(role as UserRole)) {
    return true;
  }

  // adminPanel is Admin/Owner only
  if (permission === 'adminPanel') {
    return false;
  }

  // User role: very limited permissions
  if (role === 'User') {
    // Users can only view their own submissions (handled at query level)
    // They get viewSubmissions to see their own, but nothing else
    return permission === 'viewSubmissions';
  }

  // Check tier feature requirement (applies to both Manager and Editor)
  const requiredTierFeature = PERMISSION_TIER_MAP[permission];
  if (requiredTierFeature && tierFeatures) {
    if (!tierFeatures[requiredTierFeature]) {
      return false;
    }
  }

  // Manager: all permissions within their tier's allowance
  if (role === 'Manager') {
    return true;
  }

  // Editor: check company editor permissions
  if (role === 'Editor') {
    const requiredEditorPerm = PERMISSION_EDITOR_MAP[permission];
    if (requiredEditorPerm) {
      const perms = editorPermissions || DEFAULT_EDITOR_PERMISSIONS;
      return perms[requiredEditorPerm];
    }
    // manageMembers and manageCompany are Manager+ only
    if (permission === 'manageMembers' || permission === 'manageCompany') {
      return false;
    }
    // viewAllForms: Editors can see company forms if they have viewSubmissions
    if (permission === 'viewAllForms') {
      const perms = editorPermissions || DEFAULT_EDITOR_PERMISSIONS;
      return perms.canViewSubmissions;
    }
    return false;
  }

  return false;
}

/**
 * Check if a role is a system admin (Admin/Owner).
 */
export function isSystemAdmin(role: UserRole | string | null | undefined): boolean {
  if (!role) return false;
  return SYSTEM_ADMIN_ROLES.includes(role as UserRole);
}

/**
 * Check if a role can manage a company (Admin/Owner/Manager).
 */
export function isCompanyManager(role: UserRole | string | null | undefined): boolean {
  if (!role) return false;
  return role === 'Admin' || role === 'Owner' || role === 'Manager';
}

/**
 * Get a human-readable label for a role.
 */
export function getRoleLabel(role: UserRole | string): string {
  switch (role) {
    case 'Admin': return 'System Admin';
    case 'Owner': return 'System Owner';
    case 'Manager': return 'Manager';
    case 'Editor': return 'Editor';
    case 'User': return 'User';
    default: return role;
  }
}

/**
 * Get the list of permissions a role has, given the company settings.
 */
export function getPermissionsForRole(
  role: UserRole | string,
  editorPermissions?: EditorPermissions | null,
  tierFeatures?: TierFeatures | null
): Permission[] {
  const allPermissions: Permission[] = [
    'viewAllForms',
    'editForms',
    'viewSubmissions',
    'shareForms',
    'archiveSubmissions',
    'viewAnalytics',
    'exportData',
    'manageApprovals',
    'useLandingPage',
    'manageMembers',
    'manageCompany',
    'adminPanel',
  ];

  return allPermissions.filter(p =>
    hasPermission(role, p, editorPermissions, tierFeatures)
  );
}
